import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import compose from 'recompose/compose';
import { withStyles } from '@material-ui/core';
import WarningIcon from "@material-ui/icons/Warning";
import { bool, func, shape, arrayOf, object, string } from 'prop-types';
import { get, isEmpty, each, difference, keys } from 'lodash';

import {
  AlertDialog,
  FormActionButtons,
} from '../../../../components';
import { RateQuotaSelect } from '../../../../pageComponents';
import {
  fetchTiersProduct,
  saveTiersProduct,
  resetFetchTiersProduct,
} from '../../../../actions/products';
import {
  getIsTiersProductSaveSuccess,
  getTiersProduct,
  getIsFetchTiersProduct,
} from '../../../../reducers/products';
import styles from './styles';
import ListContainer from '../../../list';
import {
  ALERT_SUCCESS,
  QUOTA_ASSIGNMENT_PRODUCT_PER_APPLICATION,
  TIERS_PRODUCT_ASSIGNED,
  TIERS_PRODUCT_NOT_ASSIGNED,
  PERMISSION_TIERS_PRODUCT,
  PERMISSION_TIER,
  PERMISSION_TIERS_RATE_QUOTA,
} from '../../../../constants';
import { getI18n } from '../../../../utils/intl';
import { hasCRUDPermissions } from '../../../../utils';
import { getRateLimitQuotas } from '../../../../reducers/api';
import { fetchRateLimitQuotas } from '../../../../actions/api';
import getListColumns from './columns';
import { getFeatureFlagForDistributeRateLimit } from '../../../../reducers/portalConfig';

const EDIT_MODE = 'EDIT_MODE';
const ADD_MODE = 'ADD_MODE';

export const RateQuota = (props) => {
  const {
    classes,
    isLoading,
    productUuid,
    tiersProduct,
    rateLimitQuotas,
    isTiersProductSaveSuccess,
    fetchTiersProduct,
    fetchRateLimitQuotas,
    resetFetchTiersProduct,
    isFetchTiersProduct,
    saveTiersProduct,
    isCurrentTab,
    notifyMessages,
    isError,
    featureFlagDistributeRateLimit,
    userContext,
  } = props;

  const intl = getI18n(useIntl());

  const [hasChanges, setHasChanges] = useState(false);
  const [newPayload, setNewPayload] = useState([]);
  const [showRateQuotaDialog, setShowRateQuotaDialog] = useState(false);
  const [selectedRateLimitQuota, setSelectedRateLimitQuota] = useState({});
  const [editingTierUuid, setEditingTierUuid] = useState('');
  const [mode, setMode] = useState(EDIT_MODE);
  const [modifiedRecords, setModifiedRecords] = useState(0);
  const hasUpdate = hasCRUDPermissions(userContext, ['UPDATE'], PERMISSION_TIERS_PRODUCT);
  const hasRead = hasCRUDPermissions(userContext, ['READ'], PERMISSION_TIERS_PRODUCT);
  const hasRateQuotaRead = hasCRUDPermissions(userContext, ['READ'], PERMISSION_TIERS_RATE_QUOTA);
  const hasTierRead = hasCRUDPermissions(userContext, ['READ'], PERMISSION_TIER);

  useEffect(() => {
    if (isTiersProductSaveSuccess) {
      setHasChanges(false);
      if(mode === EDIT_MODE){
        fetchTiersByAccess();
      } else{
        setMode(EDIT_MODE);
      }
      notifyMessages(
        ALERT_SUCCESS,
        intl.getI18nMessage('label.product.tiers.update.successful'),
      );
      setTimeout(() => {
        notifyMessages('', '');
      }, 2500);
    }
  }, [isTiersProductSaveSuccess]);

  useEffect(() => {
    if (isCurrentTab) {
      if(!hasRead) {
        props.push('/404');
      }
      if (hasRateQuotaRead) {
        fetchRateLimitQuotas(2000, 0, QUOTA_ASSIGNMENT_PRODUCT_PER_APPLICATION);
      }
    }
  }, [isCurrentTab]);

  useEffect(() => {
    if (isCurrentTab) {
      if(!hasRead) {
        props.push('/404');
      }
      fetchTiersByAccess();
    }
  },[isCurrentTab, mode])

  useEffect(() => {
    if (isError && isCurrentTab) {
      setHasChanges(true);
    } else {
      setHasChanges(false);
    }
  }, [isCurrentTab, isError]);

  useEffect(() => {
    // Reformat the payload to match the API PATCH request
    if (tiersProduct?.length > 0 && isFetchTiersProduct) {
      resetFetchTiersProduct();
      const initialPayload = [];
      tiersProduct.forEach((tierProduct) => {
        initialPayload.push({
          ...tierProduct,
          rateQuotaUuid: tierProduct.rateQuota?.uuid,
        });
      });
      setNewPayload(initialPayload);
    }
    if(tiersProduct?.length === 0 && isFetchTiersProduct){
      resetFetchTiersProduct();
      setNewPayload([]);
    }
  }, [tiersProduct]);

  useEffect(() => {
    const tiersProductMap = {};
    each(tiersProduct, item => {
      tiersProductMap[item.uuid] = item;
    });
    const newPayloadMap = {};
    each(newPayload, item => {
      newPayloadMap[item.uuid] = item;
    });

    const tiersProductIds = keys(tiersProductMap);
    const newPayloadIds = keys(newPayloadMap);
    if ( difference(tiersProductIds, newPayloadIds).length !== 0) {
      return ;
    }
    let modifiedRecords = 0;
    each(tiersProductIds, id => {
      if (tiersProductMap[id].productTierAccessStatus !==
        newPayloadMap[id].productTierAccessStatus) {
        modifiedRecords++;
      }else if(mode === EDIT_MODE){
        if(tiersProductMap[id].rateQuota.uuid !==
          newPayloadMap[id].rateQuotaUuid ||
          tiersProductMap[id].isDefaultTier !==
          newPayloadMap[id].isDefaultTier)
        modifiedRecords++;
      }
    });
    if(modifiedRecords === 0){
      setHasChanges(false);
    }
    setModifiedRecords(modifiedRecords);
  }, [newPayload]);

  const onModalClose = () => {
    setEditingTierUuid('');
    setSelectedRateLimitQuota({});
    setShowRateQuotaDialog(false);
  };

  // Assign the rateQuota to the newPayload to fit GET format from API
  const assignRateQuotaToNewPayload = (newPayload, index, rateLimitQuota) => {
    newPayload[index].rateQuota && delete newPayload[index].rateQuota;
    newPayload[index].rateQuotaUuid = rateLimitQuota.uuid;
    newPayload[index].rateQuota = {
      uuid: rateLimitQuota.uuid,
      name: rateLimitQuota.name,
      quota: rateLimitQuota.quota.quota,
      quotaInterval: rateLimitQuota.quota.interval,
      rateLimit: rateLimitQuota.rateLimit.maxRequestsPerSecond,
      isDefault: rateLimitQuota.defaultRateQuota,
    };
    if(featureFlagDistributeRateLimit) {
      newPayload[index].rateQuota.distributedRateLimit =
        get(rateLimitQuota, 'distributedRateLimit.maxRequests');
      newPayload[index].rateQuota.distributedRateLimitInterval =
       get(rateLimitQuota, 'distributedRateLimit.interval');
    }
    newPayload[index].isModified = true;
    newPayload[index].productTierAccessStatus = TIERS_PRODUCT_ASSIGNED;
  };

  const updateRateQuota = () => {
    localStorage.setItem('isUnSavedChanges-products', true);
    if (!isEmpty(selectedRateLimitQuota)) {
      setNewPayload((newPayload) => {
        const index = newPayload.findIndex(
          (payload) => payload.tierUuid === editingTierUuid,
        );

        if (index > -1 && selectedRateLimitQuota) {
          assignRateQuotaToNewPayload(
            newPayload,
            index,
            selectedRateLimitQuota,
          );
        }

        return [...newPayload];
      });

      // If initialTier rateQuota uuid !== selectedRateLimitQuota uuid show save footer
      const initialTier = tiersProduct.find(
        (tierProduct) => tierProduct.tierUuid === editingTierUuid,
      );

      if (initialTier.rateQuota?.uuid !== selectedRateLimitQuota.uuid) {
        setHasChanges(true);
      }
    }

    onModalClose();
  };

  const onSave = async () => {
    localStorage.setItem('isUnSavedChanges-products', false);
    const unassignPayload = newPayload.filter(
      (payload) =>
        payload.productTierAccessStatus === TIERS_PRODUCT_NOT_ASSIGNED && payload.isModified,
    );
    const updatePayload = newPayload.filter(
      (payload) => payload.productTierAccessStatus === TIERS_PRODUCT_ASSIGNED && payload.isModified,
    );

    // Avoid sending remove request for already unassigned tiersProduct
    tiersProduct.forEach((tierProduct) => {
      if (tierProduct.productTierAccessStatus === TIERS_PRODUCT_NOT_ASSIGNED) {
        const index = unassignPayload.findIndex(
          (payload) => payload.tierUuid === tierProduct.tierUuid && !!payload.isModified,
        );
        if (index > -1) {
          unassignPayload.splice(index, 1);
        }
      }
    });

    if(updatePayload.length > 0){
      await saveTiersProduct(productUuid, updatePayload);
    }
    if (unassignPayload.length > 0) {
      await saveTiersProduct(productUuid, unassignPayload, 'remove');
    }
  };

  const onCancel = () => {
    localStorage.setItem('isUnSavedChanges-products', false);
    setHasChanges(false);
    if(mode === EDIT_MODE){
      fetchTiersByAccess();
    }else{
      setMode(EDIT_MODE);
    }
  }

  const onRateLimitSelect = (rateLimitQuota) => {
    setSelectedRateLimitQuota(rateLimitQuota);
  };

  const addButtonLabel = intl.getI18nMessage('label.product.rate.limit.quota.add.button.title');
  const handleAddButton = () => {
    setMode(ADD_MODE);
  }

  const fetchTiersByAccess = ({
    access = mode === EDIT_MODE ? TIERS_PRODUCT_ASSIGNED : TIERS_PRODUCT_NOT_ASSIGNED,
  } = {}) => {
    fetchTiersProduct(productUuid, access);
  };

  const showFormActionButtons = (mode === ADD_MODE || hasChanges);

  const showAddBtn = (mode === EDIT_MODE && hasUpdate);

  const RateLimitQuotaSelect = () => (
    <RateQuotaSelect
      rateLimitQuotas={rateLimitQuotas}
      selectedRateLimitQuota={selectedRateLimitQuota}
      onRateLimitSelect={onRateLimitSelect}
      isSecondaryText={true}
      loading={true}
      id='tiers-product-rateQuota-select'
      labelText={intl.getI18nMessage('label.search.button')}
      featureFlagDistributeRateLimit={featureFlagDistributeRateLimit}
    />
  );

  const assistText = hasChanges ? (<span>
    {intl.getI18nMessage('label.product.rate.limit.quota.save.assist.text', { num: modifiedRecords })}
    <WarningIcon className={classes.warningIcon} />
    {intl.getI18nMessage('label.redepolyment.required')}
  </span>) : '';

  return (
    <div
      id="tiers-product-container"
      data-apim-test="tiers-product-container"
      className={classes.tiersProductContainer}
    >
      {showRateQuotaDialog && (
        <AlertDialog
          id="tiers-product-list-rateQuota-dialog"
          isOpen={showRateQuotaDialog}
          title={intl.getI18nMessage('label.rate.limit.quotas')}
          component={<RateLimitQuotaSelect />}
          submitText={intl.getI18nMessage('label.apply.button')}
          cancelText={intl.getI18nMessage('label.cancel.button')}
          onClose={onModalClose}
          onSubmit={updateRateQuota}
          onCancel={onModalClose}
          containerClass={classes.dialogContainer}
          dialogActionClass={classes.dialogActionClass}
        />
      )}
      <ListContainer
        listPageId="tiers-product"
        isLoading={isLoading}
        pageHeaderTitle={intl.getI18nMessage(
          'label.product.tiers.list.page.title',
        )}
        pageHeaderDescription={intl.getI18nMessage(
          'label.product.tiers.list.page.description',
        )}
        showListHeader={true}
        columns={getListColumns(
          classes,
          setHasChanges,
          setNewPayload,
          setShowRateQuotaDialog,
          setSelectedRateLimitQuota,
          rateLimitQuotas,
          setEditingTierUuid,
          assignRateQuotaToNewPayload,
          featureFlagDistributeRateLimit,
          hasUpdate,
          hasTierRead,
          hasRateQuotaRead,
        )}
        rows={newPayload}
        hasPaginationDisabled={true}
        classes={classes}
        pageClass={classes.page}
        onAdd={showAddBtn ? handleAddButton : null}
        addButtonLabel={addButtonLabel}
        disableAdd={hasChanges && mode === EDIT_MODE}
      />
      <FormActionButtons
        assistText={assistText}
        isDisabled={!hasChanges}
        isContainerFooter={true}
        onNextClick={onSave}
        onCancelClick={onCancel}
        nextText={intl.getI18nMessage('label.save.button')}
        cancelText={intl.getI18nMessage('label.cancel.button')}
        id="product-details-ratequota-save-footer"
        data-apim-test="product-details-ratequota-save-footer"
        containerClass={showFormActionButtons ?
          classes.showFooterContainer : classes.hideFooterContainer}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  isLoading: false,
  tiersProduct: getTiersProduct(state),
  rateLimitQuotas: getRateLimitQuotas(state),
  isTiersProductSaveSuccess: getIsTiersProductSaveSuccess(state),
  isFetchTiersProduct: getIsFetchTiersProduct(state),
  featureFlagDistributeRateLimit: getFeatureFlagForDistributeRateLimit(state),
});

const mapDispatchToProps = {
  fetchTiersProduct,
  fetchRateLimitQuotas,
  resetFetchTiersProduct,
  saveTiersProduct,
  push,
};

RateQuota.propTypes = {
  classes: shape({}),
  isLoading: bool,
  productUuid: string,
  tiersProduct: arrayOf(object),
  rateLimitQuotas: arrayOf(object),
  isTiersProductSaveSuccess: bool,
  fetchTiersProduct: func,
  fetchRateLimitQuotas: func,
  saveTiersProduct: func,
  isCurrentTab: bool,
  push: func,
  notifyMessages: func,
  resetFetchTiersProduct: func,
  isFetchTiersProduct: bool,
  isError: bool,
  featureFlagDistributeRateLimit: bool,
  userContext: arrayOf(object),
};

RateQuota.displayName = 'TiersProduct';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(RateQuota);
